import { setRouteHistory } from 'modules/common/commonSlice';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { getHealthPlanById, getPriceListById } from 'modules/categories/api';
import { fetchSelectedPriceListDetailsSuccess } from 'modules/categories/categoriesSlice';

const useCustomNavigation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const routeHistory = useSelector(state => state.common.routeHistory);
  const location = useLocation();

  const handleNavigation = (path, label) => {
    const currentRoute = location.pathname;
    const selectedIndex = routeHistory.findIndex(route => route.path === path);
    let newPath;

    if (selectedIndex !== -1) {
      const newRouteHistory = routeHistory.slice(0, selectedIndex + 1);
      newPath = path;
      dispatch(setRouteHistory(newRouteHistory));
    } else {
      newPath = currentRoute + path;
      dispatch(setRouteHistory([...routeHistory, { path: newPath, label }]));
    }

    navigate(newPath);
  };

  const goBack = () => {
    if (routeHistory.length > 1) {
      const previousRoute = routeHistory[routeHistory.length - 2];
      dispatch(setRouteHistory(routeHistory.slice(0, -1)));
      navigate(previousRoute.path);
    }
  };

  const generateBreadcrumbsFromPath = async path => {
    const segmentLabelMap = {
      'health-plans': 'Health Plans',
      'doctor-consultations-price-lists': 'Doctor Consultations',
      'pharmacies-price-lists': 'Pharmacies',
      'labs-price-lists': 'Labs',
      'scans-price-lists': 'Scans',
      'clinical-services-price-lists': 'InClinic Services',
      'surgical-price-lists': 'Surgical',
      'provider-discounts': 'Medical Network Discounts',
    };

    const dynamicSegmentLabelMap = {
      'health-plans': 'Plan',
      'doctor-consultations-price-lists': 'Price list',
      'labs-price-lists': 'Price list',
      'scans-price-lists': 'Price list',
      'pharmacies-price-lists': 'Price list',
      'clinical-services-price-lists': 'Price list',
      'surgical-price-lists': 'Price list',
    };

    const segments = path.split('/').filter(segment => segment !== '');

    const breadcrumbs = await Promise.all(
      segments.map(async (segment, index) => {
        const isDynamicParameter = /^\d+$/.test(segment);
        let label;

        if (isDynamicParameter) {
          if (
            dynamicSegmentLabelMap[segments[index - 1]] === dynamicSegmentLabelMap['health-plans']
          ) {
            const response = await getHealthPlanById(segments[index]);
            label = response?.data?.data?.name || dynamicSegmentLabelMap[segments[index - 1]];
          } else {
            const response = await getPriceListById(segments[index]);
            dispatch(fetchSelectedPriceListDetailsSuccess(response?.data?.data));
            label = response?.data?.data?.name || dynamicSegmentLabelMap[segments[index - 1]];
          }
        } else {
          label = segmentLabelMap[segment];
        }

        return {
          path: `/${segments.slice(0, index + 1).join('/')}`,
          label,
        };
      }),
    );

    dispatch(setRouteHistory(breadcrumbs));
  };

  useEffect(() => {
    generateBreadcrumbsFromPath(location.pathname);
  }, [location.pathname]);

  return {
    handleNavigation,
    goBack,
    routeHistory,
  };
};

export default useCustomNavigation;
